import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { fabric } from 'fabric';
import { VISIBLE_WHEN_VALUES } from 'modules/manager/domain/placeholder/types';
import type { VisibleWhen } from 'modules/manager/domain/placeholder/types';
import { TemplateType } from 'modules/manager/domain/template-type';
import React, { useCallback, useMemo } from 'react';
import type { MessageDescriptor } from 'shared/i18n';
import type { ObjectInspectorProps } from '../types';
import CommitOptionalNumberField from './commit-optional-number-field';
import HelpBubble from './help-bubble';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import SelectField from './select-field';
import { useCommonFabricData } from './use-common-fabric-data';

const VISIBLE_WHEN_LABELS: Record<VisibleWhen, MessageDescriptor> = {
  winner: msg`Команда прошла дальше`,
  loser: msg`Команда выбыла`,
  played: msg`Пара сыграна`,
  not_played: msg`Пара не сыграна`,
};

const MIXED_VALUE = '__mixed__';

/**
 * «Условие показа» — `data.visibleWhen` of a bracket object: the renderer drops
 * the object when the condition is false for its pair (winner / loser of side
 * `teamNumber`, pair played / not played). Only on PLAYOFF_BRACKET for now; the
 * contract itself does not depend on the type.
 *
 * A decor object (rect, shape, text) has no «Идентификация» section, so its pair
 * (`matchNumber`) and side (`teamNumber`) are set right here; a placeholder
 * already edits both in «Идентификация», and the section shows only the
 * condition to avoid a duplicate field.
 */
const VisibleWhenSection: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const { t } = useLingui();
  const MIXED = t`(разное)`;
  const visibleWhen = useCommonFabricData<VisibleWhen>(objects, 'visibleWhen', editor);
  const matchNumber = useCommonFabricData<number>(objects, 'matchNumber', editor);
  const teamNumber = useCommonFabricData<number>(objects, 'teamNumber', editor);

  const templateType = editor.placeholders?.getTemplateType();
  const allPlaceholders = objects.length > 0 && objects.every((o) => editor.placeholders?.isPlaceholder(o));

  const refitPlaceholders = useCallback(() => {
    const facade = editor.placeholders;
    if (!facade) return;
    for (const obj of objects) {
      if (facade.isPlaceholder(obj)) facade.refit(obj as fabric.Group);
    }
  }, [editor, objects]);

  const options = useMemo(() => {
    const base = [
      { value: '', label: t`Всегда` },
      ...VISIBLE_WHEN_VALUES.map((value) => ({ value, label: t(VISIBLE_WHEN_LABELS[value]) })),
    ];
    return visibleWhen.isMixed ? [{ value: MIXED_VALUE, label: MIXED, disabled: true }, ...base] : base;
  }, [visibleWhen.isMixed, MIXED, t]);

  if (templateType !== TemplateType.PLAYOFF_BRACKET || objects.length === 0) return null;

  return (
    <InspectorSection title={t`Условие показа`}>
      {!allPlaceholders && (
        <InspectorRow label={t`Матч №`}>
          <CommitOptionalNumberField
            value={matchNumber.isMixed ? undefined : matchNumber.value}
            placeholder={matchNumber.isMixed ? MIXED : '—'}
            min={1}
            onCommit={(v) => {
              matchNumber.setValue(v);
              refitPlaceholders();
            }}
          />
        </InspectorRow>
      )}
      {!allPlaceholders && (
        <InspectorRow label={t`Команда №`}>
          <CommitOptionalNumberField
            value={teamNumber.isMixed ? undefined : teamNumber.value}
            placeholder={teamNumber.isMixed ? MIXED : '—'}
            min={1}
            max={2}
            onCommit={(v) => {
              teamNumber.setValue(v);
              refitPlaceholders();
            }}
          />
        </InspectorRow>
      )}
      <InspectorRow label={t`Показывать`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SelectField
              value={visibleWhen.isMixed ? MIXED_VALUE : (visibleWhen.value ?? '')}
              onChange={(v) => {
                if (v === MIXED_VALUE) return;
                visibleWhen.setValue(v ? (v as VisibleWhen) : undefined);
                refitPlaceholders();
              }}
              options={options}
            />
          </div>
          <HelpBubble
            content={
              <span>
                <Trans>
                  Объект попадает на картинку, только если условие выполнено для его пары (раунд сетки + «Матч №»). «Команда прошла
                  дальше» и «Команда выбыла» смотрят на сторону пары из «Команда №»: <code>1</code> — верхняя, <code>2</code> — нижняя.
                  Так подсвечивают прошедшего дальше и прячут счёт у несыгранной пары.
                </Trans>
              </span>
            }
          />
        </div>
      </InspectorRow>
    </InspectorSection>
  );
};

export default VisibleWhenSection;
